/* eslint-disable react/jsx-no-bind */
import React, { useCallback, useMemo, useEffect } from 'react';
import { Domanda, ElencoDominioResponse } from 'azshared/build/main/lib/api/generated/dob';
import { UseFormReturn } from 'react-hook-form/dist/types';
import { Controller } from 'react-hook-form';
import { RootState, useAppSelector, mifidMessages } from 'azshared';
import { AppSelect } from '../../../../../components/select/app-select';
import { AppRadioButtonConfig } from '../../../../../components/radio/app-radio-button';
import { AppRadioGroup } from '../../../../../components/radio/app-radio-group';
import { AppCheckboxGroup } from '../../../../../components/checkbox/app-checkbox-group';
import { useIntl } from 'react-intl';
import './survey-question.scss';

interface SurveyQuestionProps {
  form: UseFormReturn;
  question?: Domanda;
  subQuestion?: Domanda;
  readonly?: boolean;
}

export const SurveyQuestion = ({ form, question, subQuestion, readonly = false }: SurveyQuestionProps) => {
  const domains = useAppSelector((state: RootState) => state.features.dob.utility?.getDominioResponse as ElencoDominioResponse);
  const intl = useIntl();
  const { control, trigger, watch, setValue } = form;
  const type = useMemo(() => question?.inputType ?? '', [question]);
  const required = useMemo(() => question?.flagMandatory, [question]);
  const name = useMemo(() => `survey-${question?.idDomanda}`, [question]);

  const isDependencyDisabled = (() => {
    if (!question?.listaDipendenze?.length) return false;

    const conditionalIds = [255, 290, 293, 296, 299];
    if (!conditionalIds.includes(question.idDomanda || 0)) return false;

    return question.listaDipendenze.some((dep) => {
      if (dep.tipoDipendenza === 'disable' && dep.idDomandaDipendenza && dep.idRispostaDipendenza) {
        const val = watch(`survey-${dep.idDomandaDipendenza}`);
        if (!val) return true;
        const values = (val as string).split(',');
        return values.includes(dep.idRispostaDipendenza.toString());
      }
      return false;
    });
  })();

  const isQuestionReadOnly = (question as any)?.isReadOnly;
  const isDisabled = readonly || isDependencyDisabled || isQuestionReadOnly;

  useEffect(() => {
    if (isDependencyDisabled) {
      setValue(name, '', { shouldValidate: true });
    }
  }, [isDependencyDisabled, name, setValue]);

  const label = useMemo(() => {
    let text = question?.testoDomanda ?? '';
    const subText = question?.subTestoDomanda ?? '';

    if (question?.flagMandatory && !text.includes('*')) {
      text = text + '*';
    }

    // Domanda 4.2.2 (255) - controlla 4.2 (166)
    if (question?.idDomanda === 255) {
      const question166Value = form.watch('survey-166');
      const isRequired = question166Value && question166Value !== '193'; // Non "LUNGO"

      if (isRequired && !text.includes('*')) {
        text = text + '*';
      } else if (!isRequired && text.includes('*') && !question?.flagMandatory) {
        text = text.replace(/\*$/, '');
      }
    }

    return `${text}${subText ? '\n' : ''}${subText}`;
  }, [question, form]);

  const dropDownOptions = useMemo(() => {
    let domain = question?.tipoDominio;
    if (domain === 'DOM_PROFESSIONE_ATTUALE') domain = 'DOM_PROFESSIONE_ATTUALE_MIFID';
    if (!domain) {
      return (
        question?.listaRisposte?.map((r) => ({
          key: r.idRisposta?.toString() ?? '',
          label: `${r.descRisposta} ${r.descSubRisposta || ''}`
        })) ?? []
      );
    }

    const domainValues = domains.listaDomini?.find((d) => d.idDominio === domain)?.valoriDominio;
    if (domainValues) {
      const options = [];
      for (const key in domainValues) {
        options.push({ key, label: domainValues[key] });
      }
      return options;
    }
    return [];
  }, [question, domains]);

  const radioButtonOptions = useMemo(() => {
    if (!question?.listaRisposte) return [];
    return question.listaRisposte.map((r) => ({
      id: r.idRisposta?.toString(),
      value: r.idRisposta?.toString(),
      label: `${r.descRisposta}${r.descSubRisposta ? '<br />' + r.descSubRisposta : ''}`
    })) as AppRadioButtonConfig[];
  }, [question]);

  const checkboxOptions = useMemo(() => {
    if (!question?.listaRisposte) return [];
    return question.listaRisposte.map((r) => ({
      id: r.idRisposta?.toString() ?? '',
      label: `${r.descRisposta} ${r.descSubRisposta || ''}`
    }));
  }, [question]);

  // ----------------------------
  // Render functions with `any` type
  const renderAppSelect = useCallback(
    (props: any) => {
      const { field, fieldState } = props;
      const error = fieldState?.error;

      return (
        <div>
          <h4 className={isDisabled ? 'question-disabled' : ''}>{label}</h4>
          <AppSelect
            onChange={(val) => {
              field.onChange(val);
              trigger();
            }}
            valid={!error?.message}
            value={field.value}
            required={required}
            options={dropDownOptions.map((o) => ({ type: o.key, label: o.label }))}
            disabled={isDisabled}
          />
        </div>
      );
    },
    [dropDownOptions, label, trigger, required, isDisabled]
  );

  const renderAppGroupYesNo = useCallback(
    (props: any) => {
      const { field, fieldState } = props;
      const error = fieldState?.error;

      return (
        <div className='survey-question-yes-no'>
          <div className={isDisabled ? 'question-disabled' : ''}>
            {question?.testoDomanda && <h4 className={question?.subTestoDomanda ? 'flexSubDomanda' : ''}>{question.testoDomanda}</h4>}
            {question?.subTestoDomanda && <h6>{question.subTestoDomanda}</h6>}
          </div>
          <AppRadioGroup
            options={radioButtonOptions.map((o) => ({ value: o.value, label: o.label }))}
            selectedValue={field?.value}
            valid={!error}
            disabled={isDisabled}
            onSelectedValueChanged={(val) => {
              if (!isDisabled) {
                field.onChange(val);
                trigger();
              }
            }}
          />
        </div>
      );
    },
    [question, radioButtonOptions, trigger, isDisabled]
  );

  const renderAppRadioGroup = useCallback(
    (props: any) => {
      const { field, fieldState } = props;
      const error = fieldState?.error;

      const getCleanLabel = () => {
        if (!question?.testoDomanda) return '';

        const esgSubQuestionIds = [290, 293, 296, 299];
        let text = question.testoDomanda;

        if (esgSubQuestionIds.includes(question.idDomanda || 0)) {
          text = text.replace(/^5\.4\.\d+\s*-\s*/, '');

          if (question.flagMandatory && !text.includes('*')) {
            text = text + '*';
          }
        }

        return text;
      };

      return (
        <div>
          {question?.idDomanda === 290 && (
            <div className={isDisabled ? 'question-disabled' : ''}>
              <h4>{intl.formatMessage(mifidMessages.esgQuestion54Title)}</h4>
              <h4>{intl.formatMessage(mifidMessages.esgQuestion54SubtitleA)}</h4>
            </div>
          )}

          {question?.subTestoDomanda ? (
            <div className={isDisabled ? 'question-disabled' : ''}>
              <h4>{getCleanLabel()}</h4>
              <h6>{question.subTestoDomanda}</h6>
            </div>
          ) : (
            question?.testoDomanda && <h4 className={isDisabled ? 'question-disabled' : ''}>{getCleanLabel()}</h4>
          )}

          <AppRadioGroup
            options={radioButtonOptions.map((o) => {
              let formattedLabel = o.label.replace('(**) (**)', '(**)<br />(**)');

              const investmentIds = ['186', '187', '188', '189', '190'];
              if (investmentIds.includes(o.value ?? '')) {
                formattedLabel = formattedLabel.replace(/(Investimento \d+)\s+/, '$1<br />');
              }

              return { value: o.value, label: formattedLabel };
            })}
            onSelectedValueChanged={(val) => {
              if (!isDisabled) {
                field.onChange(val);
                trigger();
              }
            }}
            selectedValue={isDependencyDisabled ? undefined : field?.value}
            valid={!error}
            disabled={isDisabled}
          />
        </div>
      );
    },
    [
      isDisabled,
      question.idDomanda,
      question.subTestoDomanda,
      question.testoDomanda,
      question.flagMandatory,
      intl,
      radioButtonOptions,
      isDependencyDisabled,
      trigger
    ]
  );

  const renderCheckbox = useCallback(
    (props: any) => {
      const { field, fieldState } = props;
      const error = fieldState?.error;

      return (
        <div>
          {label && <h4 className={isDisabled ? 'question-disabled' : ''}>{label}</h4>}
          <div>
            <AppCheckboxGroup
              options={checkboxOptions.map((option) => ({ value: option.id, label: option.label }))}
              valid={!error}
              selectedValues={field.value?.split(',')}
              disabled={isDisabled}
              onSelectedValueChanged={(val) => {
                if (!isDisabled) {
                  field.onChange(val.join(','));
                }
              }}
            />
          </div>
        </div>
      );
    },
    [checkboxOptions, label, isDisabled]
  );

  const renderSubRadioCheckBox = useCallback(
    (props: any) => {
      const { field, fieldState } = props;
      const error = fieldState?.error;
      const displayValue = field.value?.split(',')[0];
      return (
        <div>
          {question?.subTestoDomanda ? (
            <div className={isDisabled ? 'question-disabled' : ''}>
              <h4>{question.testoDomanda}</h4>
              <h6>{question.subTestoDomanda}</h6>
            </div>
          ) : (
            <h4 className={isDisabled ? 'question-disabled' : ''}>{question?.testoDomanda}</h4>
          )}
          <div>
            <AppRadioGroup
              options={radioButtonOptions.map((o) => ({ value: o.value, label: o.label }))}
              selectedValue={displayValue}
              valid={!error}
              disabled={isDisabled}
              onSelectedValueChanged={(val) => {
                field.onChange(val);
                if (val?.includes('329')) {
                  form.setValue('survey-284', '');
                }
                if(val?.includes('201')){
                    form.setValue('survey-170', '');

                }
                trigger();
              }}
            />
          </div>
          <div style={{ paddingLeft: '20px' }}>
            {field.value?.includes('330') && (form.getValues('survey-284') === undefined || form.getValues('survey-284') === '') && (
              <h4 style={{ color: '#D14343' }}>{intl.formatMessage(mifidMessages.investmentMeasureTitle)}</h4>
            )}
            {field.value?.includes('276') && (form.getValues('survey-266') === undefined || form.getValues('survey-266') === '') && (
              <h4 style={{ color: '#D14343' }}>{intl.formatMessage(mifidMessages.factorTitle)}</h4>
            )}
            {question?.idDomanda?.toString() === '267' && field.value?.includes('276') && (
              <SurveyQuestion question={subQuestion} form={form} readonly={readonly} />
            )}
            {question?.idDomanda?.toString() === '285' && field.value?.includes('330') && (
              <SurveyQuestion question={{ ...subQuestion, flagMandatory: true }} form={form} readonly={readonly} />
            )}
            {question?.idDomanda?.toString() === '171' && field.value?.includes('200') && (
              <SurveyQuestion question={subQuestion} form={form} readonly={readonly} />
            )}
          </div>
        </div>
      );
    },
    [question.subTestoDomanda, question.testoDomanda, question?.idDomanda, isDisabled, radioButtonOptions, form, intl, subQuestion, readonly, trigger]
  );

  // ----------------------------
  // Rendering logic
  if (['DROPDOWN', 'DROP_NUCLEOFAMILIARE', 'DROP_STATOCIVILE'].includes(type)) {
    return <Controller name={name} control={control} render={renderAppSelect} />;
  }

  if (['YES_NO', 'GENERIC_YN'].includes(type)) {
    return <Controller name={name} control={control} render={renderAppGroupYesNo} />;
  }

  if (['RADIO_BUTTON'].includes(type)) {
    return <Controller name={name} control={control} render={renderAppRadioGroup} />;
  }

  if (['SUB_YES_NO', 'MULTIPLE_CHOICE_VERO_FALSO'].includes(type)) {
    if (
      !question?.strutturaInternaDomande ||
      !Array.isArray(question.strutturaInternaDomande.domande) ||
      !Array.isArray(question.strutturaInternaDomande.listaDominiRisposte) ||
      !question.strutturaInternaDomande.listaDominiRisposte.length
    ) {
      return <></>;
    }

    const first = question.strutturaInternaDomande.listaDominiRisposte[0];

    const title = label?.split('<br>').map((s) => (
      <>
        {s}
        <br />
      </>
    ));

    return (
      <>
        <h4 className={isDisabled ? 'question-disabled' : ''}>{title}</h4>
        {question.strutturaInternaDomande.domande.map((sub) => (
          <SurveyQuestion
            key={sub.idDomanda}
            form={form}
            question={{ ...sub, inputType: first.descrizione, listaRisposte: first.risposte }}
            readonly={readonly}
          />
        ))}{' '}
      </>
    );
  }

  if (['CHECKBOX'].includes(type)) {
    return <Controller name={name} control={control} render={renderCheckbox} />;
  }

  if (['SUB_RADIOCHECK_ESG_PF', 'SUB_RADIOCHECK_ESG_2_PF', 'SUB_RADIOCHECK_PF'].includes(type)) {
    const isRadio = type === 'SUB_RADIOCHECK_ESG_2_PF';
    if (question?.strutturaInternaDomande == null) return <></>;
    if (!Array.isArray(question.strutturaInternaDomande?.domande)) return <></>;
    if (question.strutturaInternaDomande?.domande?.length > 1) return <></>;
    const mainQuestion = question.strutturaInternaDomande?.domande[0];
    const mainAnswers = question.strutturaInternaDomande.listaDominiRisposte?.at(0)?.risposte?.filter((r) => r.listaDipendenze?.length === 0);
    const subQuestionAnswers = question.strutturaInternaDomande.listaDominiRisposte?.at(0)?.risposte?.filter((r) => r.listaDipendenze?.length !== 0);
    const mainQuestionToRender = { ...mainQuestion, listaRisposte: mainAnswers || [] } as Domanda;
    const subQuestionToRender = {
      idDomanda: question.idDomanda,
      inputType: isRadio ? 'RADIO_BUTTON' : 'CHECKBOX',
      testoDomanda: '',
      listaRisposte: subQuestionAnswers || []
    } as Domanda;
    return <SurveyQuestion question={mainQuestionToRender} subQuestion={subQuestionToRender} form={form} readonly={readonly} />;
  }

  if (['RADIO_CHECKBOX_ESG_PF', 'RADIO_CHECKBOX_2_ESG_PF', 'RADIO_CHECKBOX_PF'].includes(type)) {
    return <Controller name={name} control={control} render={renderSubRadioCheckBox} />;
  }

  return <div>Question {question?.idDomanda}</div>;
};
